import {
  getCrop,
  deleteCrop,
  updateCrop,
  addCrop,
  updateCropFruitBloom,
  updateCropCost,
  updateCropYield,
} from '@/adapters/master';
import { ICrop, ICropRes, IApiResponse } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Flex,
  notification,
  Radio,
  Table,
  TableColumnsType,
  Input,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { uniqueId } from 'lodash';

type ListKey = 'create' | 'update' | 'match';
type UpdateFunction = (data: Partial<ICrop>[]) => Promise<IApiResponse>;

const updateOptions = [
  {
    value: 'updateCropFruitBloom',
    label: 'อัพเดต FruitBloom',
    func: updateCropFruitBloom as UpdateFunction,
  },
  {
    value: 'updateCropCost',
    label: 'อัพเดต Cost',
    func: updateCropCost as UpdateFunction,
  },
  {
    value: 'updateCropYield',
    label: 'อัพเดต Yield',
    func: updateCropYield as UpdateFunction,
  },
];

type TabType = ListKey | (typeof updateOptions)[number]['value'];

const getErrorMessage = (r: unknown): string => {
  if (r instanceof Error) return r.message;
  if (typeof r === 'object' && r !== null && 'message' in r)
    return (r as { message: string }).message;
  return 'เกิดข้อผิดพลาดในการดำเนินการ';
};

interface IBatchRejection {
  status: 'rejected' | 'fulfilled';
  reason?: { refNo?: string; errors?: { errorMessage?: string } };
}

const CropPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<ICrop[]>([]);
  const [selected, setSelected] = useState<ICrop[]>([]);
  const [tab, setTab] = useState<TabType>('create');
  const [updateAction, setUpdateAction] = useState<UpdateFunction | undefined>(
    undefined,
  );
  const [searchText, setSearchText] = useState('');

  const { data, refetch, isFetching } = useQuery<ICropRes, Error>({
    queryKey: ['get-crop'],
    queryFn: getCrop,
    retry: 0,
  });

  const notifyCropErrors = (
    title: string,
    res: any,
    originalData: Partial<ICrop>[],
  ) => {
    const errors: string[] = [];
    if (Array.isArray(res?.data)) {
      (res.data as IBatchRejection[]).forEach((result, index) => {
        if (result.status === 'rejected') {
          const woLot =
            originalData[index]?.WO_LOT ||
            `Ref: ${result.reason?.refNo || index}`;
          errors.push(
            `${woLot}: ${result.reason?.errors?.errorMessage || 'Crop End Date is required (or API error)'}`,
          );
        }
      });
    } else if (res?.status !== 'OK')
      errors.push(res?.message || 'Server Error');

    if (errors.length > 0) {
      notification.error({
        message: title,
        style: { width: '500px' },
        description: (
          <ul
            style={{
              paddingLeft: '15px',
              margin: 0,
              maxHeight: '250px',
              overflowY: 'auto',
            }}
          >
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        ),
      });
      return true;
    }
    return false;
  };

  const actionCropMutation = useMutation({
    mutationFn: (payload: Partial<ICrop>[]) =>
      tab === 'create' ? addCrop(payload) : updateCrop(payload),
    onSuccess: (res, variables) => {
      if (
        !notifyCropErrors(
          tab === 'create' ? 'Sync Crop ไม่สำเร็จ' : 'Update Crop ไม่สำเร็จ',
          res,
          variables,
        )
      ) {
        notification.success({
          message: tab === 'create' ? 'Sync Crop สำเร็จ' : 'Update Crop สำเร็จ',
        });
      }
      refetch();
    },
    onError: r => notification.error({ message: getErrorMessage(r) }),
  });

  const actionUpdateSpecificCrop = useMutation({
    mutationFn: (payload: Partial<ICrop>[]) => {
      if (updateAction) return updateAction(payload);
      throw new Error('No update function selected.');
    },
    onSuccess: (res, variables) => {
      const option = updateOptions.find(opt => opt.value === tab);
      if (
        !notifyCropErrors(`อัพเดต ${option?.label} ไม่สำเร็จ`, res, variables)
      ) {
        notification.success({ message: `${option?.label} สำเร็จ` });
      }
      refetch();
    },
    onError: r => notification.error({ message: getErrorMessage(r) }),
  });

  const actionDeleteCrop = useMutation({
    mutationFn: (payload: Partial<ICrop>[]) => deleteCrop(payload),
    onSuccess: (res, variables) => {
      if (!notifyCropErrors('ลบข้อมูลไม่สำเร็จ', res, variables)) {
        notification.success({ message: 'Delete Crop สำเร็จ' });
      }
      refetch();
    },
    onError: r => notification.error({ message: getErrorMessage(r) }),
  });

  useEffect(() => {
    const listMap: Record<string, ICrop[] | undefined> = {
      create: data?.insert,
      update: data?.update,
      match: data?.match,
    };
    const isSpecificUpdate = updateOptions.some(opt => opt.value === tab);
    if (isSpecificUpdate) {
      setSelected(
        data?.update?.map(c => ({ ...c, _key: uniqueId('crop-') })) || [],
      );
      setUpdateAction(() => updateOptions.find(opt => opt.value === tab)?.func);
    } else {
      setSelected(
        listMap[tab as string]?.map(c => ({ ...c, _key: uniqueId('crop-') })) ||
          [],
      );
      setUpdateAction(undefined);
    }
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [tab, data]);

  const filteredData = useMemo(() => {
    if (!searchText) return selected;
    const lowerSearch = searchText.toLowerCase();
    return selected.filter(
      item =>
        item.WO_LOT?.toLowerCase().includes(lowerSearch) ||
        item.WO__QADC02?.toLowerCase().includes(lowerSearch) ||
        item.PT_DESC1?.toLowerCase().includes(lowerSearch) ||
        item.WO_NBR?.toLowerCase().includes(lowerSearch),
    );
  }, [selected, searchText]);

  const columns: TableColumnsType<ICrop> = useMemo(
    () => [
      {
        title: 'ลำดับ',
        width: 50,
        align: 'center',
        render: (_, __, i) => i + 1,
      },
      {
        title: 'รหัสรอบเพาะปลูก',
        dataIndex: 'WO_LOT',
        width: 140,
        sorter: (a, b) => (a.WO_LOT || '').localeCompare(b.WO_LOT || ''),
        filters: Array.from(
          new Set(selected.map(i => i.WO_LOT).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.WO_LOT === value,
        filterSearch: true,
      },
      {
        title: 'สินค้า',
        dataIndex: 'PT_DESC1',
        ellipsis: true,
        width: 200,
        sorter: (a, b) => (a.PT_DESC1 || '').localeCompare(b.PT_DESC1 || ''),
        filters: Array.from(
          new Set(selected.map(i => i.PT_DESC1).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.PT_DESC1 === value,
        filterSearch: true,
      },
      {
        title: 'เกษตรกร',
        dataIndex: 'WO__QADC02',
        width: 150,
        sorter: (a, b) =>
          (a.WO__QADC02 || '').localeCompare(b.WO__QADC02 || ''),
        filters: Array.from(
          new Set(selected.map(i => i.WO__QADC02).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.WO__QADC02 === value,
        filterSearch: true,
      },
      {
        title: 'ตำบล',
        dataIndex: 'WO__CHR02',
        width: 120,
        sorter: (a, b) => (a.WO__CHR02 || '').localeCompare(b.WO__CHR02 || ''),
        filters: Array.from(
          new Set(selected.map(i => i.WO__CHR02).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.WO__CHR02 === value,
        filterSearch: true,
      },
      {
        title: 'อำเภอ',
        dataIndex: 'WO__CHR03',
        width: 120,
        sorter: (a, b) => (a.WO__CHR03 || '').localeCompare(b.WO__CHR03 || ''),
        filters: Array.from(
          new Set(selected.map(i => i.WO__CHR03).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.WO__CHR03 === value,
        filterSearch: true,
      },
      {
        title: 'จังหวัด',
        dataIndex: 'WO__CHR04',
        width: 120,
        sorter: (a, b) => (a.WO__CHR04 || '').localeCompare(b.WO__CHR04 || ''),
        filters: Array.from(
          new Set(selected.map(i => i.WO__CHR04).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.WO__CHR04 === value,
        filterSearch: true,
      },
    ],
    [selected],
  );

  const isPending =
    actionCropMutation.isPending ||
    actionDeleteCrop.isPending ||
    actionUpdateSpecificCrop.isPending;

  return (
    <div style={{ paddingBottom: '50px' }}>
      <Flex justify="space-between" align="center" className="mb-3">
        <Radio.Group
          value={tab}
          onChange={e => setTab(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="create">เพิ่ม</Radio.Button>
          <Radio.Button value="update">แก้ไข/ลบ</Radio.Button>
          {updateOptions.map(opt => (
            <Radio.Button key={opt.value} value={opt.value}>
              {opt.label}
            </Radio.Button>
          ))}
        </Radio.Group>
        <Flex gap={12}>
          <Input.Search
            placeholder="ค้นหา รอบเพาะปลูก/ฟาร์ม/เกษตรกร/สินค้า"
            allowClear
            onSearch={setSearchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 350 }}
          />
          {tab === 'update' && (
            <button
              className="btn btn-outline-danger"
              disabled={!selectedRowKeys.length || isPending}
              onClick={() => actionDeleteCrop.mutate(selectedRows)}
            >
              ลบข้อมูล Crop
            </button>
          )}
          <button
            className="btn btn-success"
            disabled={!selectedRowKeys.length || isPending}
            onClick={() => {
              const isSpecific = updateOptions.some(opt => opt.value === tab);
              isSpecific
                ? actionUpdateSpecificCrop.mutate(selectedRows)
                : actionCropMutation.mutate(selectedRows);
            }}
          >
            {isPending
              ? 'กำลังดำเนินการ...'
              : tab === 'create'
                ? 'ส่งข้อมูลไป OTOD >'
                : 'แก้ไขข้อมูลที่ OTOD >'}
          </button>
        </Flex>
      </Flex>
      <Table<ICrop>
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
        }}
        columns={columns}
        dataSource={filteredData}
        rowKey="_key"
        size="small"
        bordered
        loading={isFetching}
        scroll={{ x: 1200 }}
        pagination={false}
        sticky={{ offsetHeader: 0 }}
        locale={{ emptyText: 'ไม่พบข้อมูลการเพาะปลูก' }}
      />
    </div>
  );
};

export default CropPage;
